import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreVertical, Pencil, Trash2, PowerOff, Power } from "lucide-react";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Header "⋮" options menu — Edit/Delete, ported from mobile's
 * option-bottom-sheet/index.js. Only rendered meaningfully for the event
 * owner (isAdmin); sub-promoters viewing someone else's event get no menu.
 *
 * An @Room can never be permanently deleted from here — Delete is replaced
 * with Deactivate/Reactivate (Event.IsClosed, via the pre-existing
 * AroomReactivationService backend feature), since deleting a Room has no
 * "undo" while deactivating does.
 */
const OptionMenu = ({ isAdmin, isARoomEvent, isRoomClosed, onEdit, onDelete, deleting, onToggleRoomActive, togglingRoomActive }) => {
  const { t } = useTranslation("eventDetails");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  if (!isAdmin) return null;

  return (
    <div ref={ref} style={s.wrap}>
      <button type="button" style={s.trigger} onClick={() => setOpen((v) => !v)} aria-label={t("optionMenu.ariaLabel")}>
        <MoreVertical size={17} strokeWidth={2.25} />
      </button>
      {open && (
        <div className="modal-pop" style={s.menu}>
          <button type="button" className="row-link" style={s.item} onClick={() => { setOpen(false); onEdit(); }}>
            <Pencil size={14} strokeWidth={2.25} /> {t("optionMenu.editEvent")}
          </button>
          {isARoomEvent ? (
            <button
              type="button"
              className="row-link"
              style={{ ...s.item, ...(isRoomClosed ? {} : s.itemDanger) }}
              onClick={() => { setOpen(false); onToggleRoomActive(); }}
              disabled={togglingRoomActive}
            >
              {isRoomClosed ? <Power size={14} strokeWidth={2.25} /> : <PowerOff size={14} strokeWidth={2.25} />}
              {isRoomClosed ? t("optionMenu.reactivateRoom") : t("optionMenu.deactivateRoom")}
            </button>
          ) : (
            <button
              type="button"
              className="row-link"
              style={{ ...s.item, ...s.itemDanger }}
              onClick={() => { setOpen(false); onDelete(); }}
              disabled={deleting}
            >
              <Trash2 size={14} strokeWidth={2.25} /> {t("optionMenu.deleteEvent")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { position: "relative" },
  trigger: {
    width: 32, height: 32, borderRadius: "50%", border: "none",
    background: "rgba(20,21,40,0.5)", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  menu: {
    position: "absolute", top: "calc(100% + 8px)", right: 0, background: color.surface,
    borderRadius: radius.md, boxShadow: shadow.lg, border: `1px solid ${color.border}`, overflow: "hidden",
    minWidth: 170, zIndex: 20,
  },
  item: {
    display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", padding: "10px 14px",
    border: "none", background: "none", color: color.textPrimary, fontSize: 13,
    fontWeight: 500, cursor: "pointer",
  },
  itemDanger: { color: color.danger500 },
};

export default OptionMenu;
